import React, { useState, useEffect, useMemo } from 'react';
import { DollarSign, Search, RefreshCw, AlertCircle, User, FileText, Check, ChevronDown, ChevronUp, Gift } from 'lucide-react';
import { API_URL } from '../config/api';
import './PendingBilling.css';

const PendingBilling = ({ onSelectStudies }) => {
    const [studies, setStudies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [expandedPatients, setExpandedPatients] = useState({});

    const fetchPendingStudies = async () => {
        try {
            setLoading(true);
            setError('');
            const token = localStorage.getItem('token');

            const res = await fetch(`${API_URL}/patient-studies/pending-billing`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (res.ok) {
                const data = await res.json();
                setStudies(data);
            } else {
                setError('Error al cargar estudios pendientes');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPendingStudies();
    }, []);

    // Group studies by patient
    const groupedByPatient = useMemo(() => {
        const filtered = studies.filter(study => {
            const fullName = `${study.patient_first_name || ''} ${study.patient_last_name || ''}`.toLowerCase();
            const studyName = (study.study_name || '').toLowerCase();
            const searchLower = search.toLowerCase();
            return fullName.includes(searchLower) || studyName.includes(searchLower);
        });

        const groups = {};
        filtered.forEach(study => {
            const patientId = study.patient_id;
            if (!groups[patientId]) {
                groups[patientId] = {
                    patient_id: patientId,
                    patient_first_name: study.patient_first_name,
                    patient_last_name: study.patient_last_name,
                    studies: []
                };
            }
            groups[patientId].studies.push(study);
        });

        return Object.values(groups);
    }, [studies, search]);

    const formatPrice = (price) => {
        if (!price) return '$0.00';
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(price);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getTotalPrice = (patientStudies) => {
        return patientStudies.reduce((sum, s) => {
            // Exempt studies don't count toward the total
            if (s.is_cost_exempt && s.is_cost_exempt !== '0' && s.is_cost_exempt !== 0) return sum;
            return sum + (parseFloat(s.study_price) || 0);
        }, 0);
    };

    const togglePatientExpand = (patientId) => {
        setExpandedPatients(prev => ({
            ...prev,
            [patientId]: !prev[patientId]
        }));
    };

    const handleSelectPatientStudies = (patientGroup) => {
        if (onSelectStudies) {
            onSelectStudies(patientGroup);
        }
    };

    const totalStudies = studies.length;
    const totalPatients = groupedByPatient.length;

    return (
        <div className="pending-billing">
            <div className="pending-billing-header">
                <div className="header-title">
                    <DollarSign size={24} />
                    <div>
                        <h3>Estudios Pendientes de Cobro</h3>
                        <p>{totalStudies} estudio{totalStudies !== 1 ? 's' : ''} en {totalPatients} paciente{totalPatients !== 1 ? 's' : ''}</p>
                    </div>
                </div>
                <button className="btn-refresh" onClick={fetchPendingStudies} disabled={loading}>
                    <RefreshCw size={16} className={loading ? 'spinning' : ''} />
                    Actualizar
                </button>
            </div>

            <div className="pending-billing-search">
                <input
                    type="text"
                    placeholder="Buscar por paciente o estudio..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {error && (
                <div className="error-message">
                    <AlertCircle size={18} />
                    {error}
                </div>
            )}

            {loading ? (
                <div className="loading-state">
                    <RefreshCw size={24} className="spinning" />
                    <p>Cargando estudios...</p>
                </div>
            ) : groupedByPatient.length === 0 ? (
                <div className="empty-state">
                    <Check size={48} />
                    <h4>No hay estudios pendientes</h4>
                    <p>Todos los estudios han sido cobrados</p>
                </div>
            ) : (
                <div className="pending-billing-grouped">
                    {groupedByPatient.map(group => (
                        <div key={group.patient_id} className="patient-group">
                            <div className="patient-group-header">
                                <div className="patient-info" onClick={() => togglePatientExpand(group.patient_id)}>
                                    <User size={20} />
                                    <div className="patient-details">
                                        <span className="patient-name">
                                            {group.patient_first_name} {group.patient_last_name}
                                        </span>
                                        <span className="study-count">
                                            {group.studies.length} estudio{group.studies.length !== 1 ? 's' : ''} pendiente{group.studies.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    {group.studies.length > 1 && (
                                        expandedPatients[group.patient_id]
                                            ? <ChevronUp size={18} className="expand-icon" />
                                            : <ChevronDown size={18} className="expand-icon" />
                                    )}
                                </div>
                                <div className="patient-total">
                                    <span className="total-amount">{formatPrice(getTotalPrice(group.studies))}</span>
                                    <button
                                        className="btn-cobrar-all"
                                        onClick={() => handleSelectPatientStudies(group)}
                                        title={`Cobrar ${group.studies.length} estudio${group.studies.length !== 1 ? 's' : ''}`}
                                    >
                                        <DollarSign size={14} />
                                        Cobrar {group.studies.length > 1 ? `(${group.studies.length})` : ''}
                                    </button>
                                </div>
                            </div>

                            {/* Study details - show if expanded or only 1 study */}
                            {(expandedPatients[group.patient_id] || group.studies.length === 1) && (
                                <div className="study-list">
                                    {group.studies.map(study => (
                                        <div key={study.id} className={`study-item ${study.is_cost_exempt && study.is_cost_exempt !== '0' && study.is_cost_exempt !== 0 ? 'exempt' : ''}`}>
                                            <FileText size={14} />
                                            <span className="study-name">{study.study_name}</span>
                                            {study.is_cost_exempt && study.is_cost_exempt !== '0' && study.is_cost_exempt !== 0 ? (
                                                <span className="study-exempt-badge"><Gift size={12} /> Exento</span>
                                            ) : null}
                                            <span className={`study-price ${study.is_cost_exempt && study.is_cost_exempt !== '0' && study.is_cost_exempt !== 0 ? 'crossed' : ''}`}>
                                                {study.is_cost_exempt && study.is_cost_exempt !== '0' && study.is_cost_exempt !== 0
                                                    ? formatPrice(0)
                                                    : formatPrice(study.study_price)}
                                            </span>
                                            <span className="study-date">{formatDate(study.requested_at)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PendingBilling;
